import { useState } from 'react';
import { Box, Heading, Tabs, TabList, TabPanels, Tab, TabPanel, Badge, Code, Text, SimpleGrid } from '@chakra-ui/react';
import * as Icons from '@components/icons';
import { Input } from '@components/form/Input';
import { TextArea } from '@components/form/TextArea';
import { EditableText } from '@components/form/EditableText';
import { ShiftTime } from '@components/form/ShiftTime';
import { Button } from '@components/button/Button';
import { Back } from '@components/button/Back';
import ConsultantImage from '@components/ConsultantImage';
import { ConsultantProfile } from '@components/ConsultantProfile';
import { CompetencePills } from '@components/competence-pill/CompetencePill';
import { ConsultantCompetencePills } from '@components/competence-pill/ConsultantCompetencePill';
import ConsultantAvatar from '@components/jobView/ConsultantAvatar';

// Component registry (will be updated as components are migrated)
const componentRegistry = {
  'Icons': [
    { name: 'AddressIcon', migrated: true },
    { name: 'BackIcon', migrated: true },
    { name: 'BellIcon', migrated: true },
    { name: 'BigDown', migrated: true },
    { name: 'BigUp', migrated: true },
    { name: 'BigWhiteCheckIcon', migrated: true },
    { name: 'BlackClose', migrated: true },
    { name: 'Check', migrated: true },
    { name: 'CheckCircleIcon', migrated: true },
    { name: 'Chevron', migrated: true },
    { name: 'Clock', migrated: true },
    { name: 'CompletedStep', migrated: true },
    { name: 'Down', migrated: true },
    { name: 'DropDownIcon', migrated: true },
    { name: 'EmptyWhiteCircle', migrated: true },
    { name: 'GreenCheck', migrated: true },
    { name: 'InProgressStep', migrated: true },
    { name: 'JobIcon', migrated: true },
    { name: 'JobReportIcon', migrated: true },
    { name: 'LeftArrow', migrated: true },
    { name: 'Lock', migrated: true },
    { name: 'ManStandingIcon', migrated: true },
    { name: 'MenuIcon', migrated: true },
    { name: 'People', migrated: true },
    { name: 'ProfileIcon', migrated: true },
    { name: 'RedClose', migrated: true },
    { name: 'RemainingStep', migrated: true },
    { name: 'RightArrow', migrated: true },
    { name: 'ScheduleIcon', migrated: true },
    { name: 'ShowMoreHorizontal', migrated: true },
    { name: 'Speaker', migrated: true },
    { name: 'StarIcon', migrated: true },
    { name: 'StatisticIcon', migrated: true },
    { name: 'SupportIcon', migrated: true },
    { name: 'TimeReportIcon', migrated: true },
    { name: 'TooltipInfoIcon', migrated: true },
    { name: 'TransparentRoundedClose', migrated: true },
    { name: 'Trash', migrated: true },
    { name: 'WhiteCheck', migrated: true },
    { name: 'WhiteClose', migrated: true },
    { name: 'WhiteCloseLight', migrated: true },
    { name: 'WorkPlaceIcon', migrated: true },
    { name: 'Zap', migrated: true },
  ],
  'Form': [
    { name: 'Input', migrated: true },
    { name: 'TextArea', migrated: true },
    { name: 'EditableText', migrated: true },
    { name: 'ShiftTime', migrated: true },
  ],
  'Buttons': [
    { name: 'Button', migrated: true },
    { name: 'Back', migrated: true },
  ],
  'Layout': [
    // Layout components will be added during Phase 2.7
  ],
  'Job Components': [
    // Job components will be added during Phase 3
  ],
  'Schedule': [
    // Schedule components will be added during Phase 3.4
  ],
  'Create Order': [
    // Create order components will be added during Phase 3.5
  ],
  'Consultant': [
    { name: 'ConsultantImage', migrated: true },
    { name: 'ConsultantProfile', migrated: true },
    { name: 'CompetencePills', migrated: true },
    { name: 'ConsultantCompetencePills', migrated: true },
    { name: 'ConsultantAvatar', migrated: true },
  ],
  'Misc': [
    // Misc components will be added during Phase 2.4
  ],
};

interface ComponentInfo {
  name: string;
  migrated: boolean;
  category: string;
}

export function Showcase() {

  // Calculate migration progress
  const allComponents: ComponentInfo[] = [];
  Object.entries(componentRegistry).forEach(([category, components]) => {
    components.forEach((comp: any) => {
      allComponents.push({ ...comp, category });
    });
  });

  const totalComponents = allComponents.length;
  const migratedComponents = allComponents.filter(c => c.migrated).length;
  const progress = totalComponents > 0 ? Math.round((migratedComponents / totalComponents) * 100) : 0;

  return (
    <Box p={8}>
      <Heading mb={4}>Component Showcase</Heading>
      <Text fontSize="lg" mb={2}>
        This page serves as a living component library for the Next.js to Vite migration.
      </Text>
      <Badge colorScheme="green" fontSize="lg" mb={6}>
        Migration Progress: {migratedComponents}/{totalComponents} {totalComponents > 0 && `(${progress}%)`}
      </Badge>

      {totalComponents === 0 ? (
        <Box p={8} bg="gray.50" borderRadius="md">
          <Heading size="md" mb={2}>Getting Started</Heading>
          <Text>
            Components will appear here as they are migrated from the Next.js app.
            The showcase page is ready to display components once Phase 2 begins.
          </Text>
        </Box>
      ) : (
        <Tabs variant="soft-rounded" colorScheme="green">
          <TabList flexWrap="wrap">
            {Object.keys(componentRegistry).map(cat => (
              <Tab key={cat}>
                {cat}
              </Tab>
            ))}
          </TabList>

          <TabPanels>
            {Object.entries(componentRegistry).map(([cat, comps]) => (
              <TabPanel key={cat}>
                {comps.length === 0 ? (
                  <Box p={4} bg="gray.50" borderRadius="md">
                    <Text>No components in this category yet.</Text>
                  </Box>
                ) : cat === 'Icons' ? (
                  <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4}>
                    {comps.map((comp: any) => (
                      <IconCard key={comp.name} {...comp} />
                    ))}
                  </SimpleGrid>
                ) : (
                  <Box display="grid" gap={6}>
                    {comps.map((comp: any) => (
                      <ComponentCard key={comp.name} {...comp} category={cat} />
                    ))}
                  </Box>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
}

// Icon card for compact icon display
function IconCard({ name, migrated }: { name: string; migrated: boolean }) {
  const IconComponent = migrated ? (Icons as any)[name] : null;

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      p={4}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="120px"
      _hover={{ borderColor: 'green.500', boxShadow: 'sm' }}
      transition="all 0.2s"
    >
      {IconComponent && (
        <Box mb={2}>
          <IconComponent width={32} height={32} />
        </Box>
      )}
      <Text fontSize="xs" textAlign="center" color="gray.600">
        {name}
      </Text>
      {migrated && (
        <Badge mt={1} colorScheme="green" size="sm">✓</Badge>
      )}
    </Box>
  );
}

// Component card for displaying individual components
function ComponentCard({ name, migrated, category }: ComponentInfo) {
  const [inputValue, setInputValue] = useState('');
  const [textAreaValue, setTextAreaValue] = useState('');
  const [editableValue, setEditableValue] = useState('Click to edit this text');
  const [shiftTimeValue, setShiftTimeValue] = useState<{ start: Date; end: Date } | undefined>();

  // Mock consultant data
  const mockConsultant = {
    id: '123',
    firstName: 'Anna',
    lastName: 'Andersson',
    profile: {
      presentation: 'Erfaren sjuksköterska med specialistkompetens inom akutsjukvård. Har arbetat inom vården i över 10 år med fokus på patientsäkerhet och kvalitetsutveckling.',
      profilePicture: '',
      competences: [
        { branch: 'Sjukvård', service: 'Akutsjukvård', skills: ['Triagering', 'Akut omhändertagande'] }
      ]
    }
  };

  const mockJobCompetences = [
    { branch: 'Sjukvård', service: 'Akutsjukvård', skills: ['Triagering'] }
  ];

  if (!migrated) {
    return (
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
        <Heading size="md" mb={2}>
          {name}
          <Badge ml={2} colorScheme="gray">Not Yet Migrated</Badge>
        </Heading>
        <Code fontSize="sm" mb={2}>
          src/components/{category.toLowerCase()}/{name}.tsx
        </Code>
        <Box mt={4} p={4} bg="gray.100" borderRadius="md" color="gray.500">
          Component not yet migrated
        </Box>
      </Box>
    );
  }

  // Render preview based on component type
  const renderPreview = () => {
    switch (name) {
      case 'Input':
        return (
          <Input
            label="Sample Input"
            placeholder="Type something..."
            value={inputValue}
            onChangeText={setInputValue}
          />
        );

      case 'TextArea':
        return (
          <TextArea
            label="Sample TextArea"
            placeholder="Type multiple lines..."
            value={textAreaValue}
            onChangeText={setTextAreaValue}
          />
        );

      case 'EditableText':
        return (
          <EditableText
            label="Sample Editable Text"
            value={editableValue}
            onChange={setEditableValue}
          />
        );

      case 'ShiftTime':
        return (
          <ShiftTime
            value={shiftTimeValue}
            onChange={setShiftTimeValue}
            day={new Date()}
          />
        );

      case 'Button':
        return (
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button type="brand" title="Brand Button" onClick={() => alert('Clicked!')} />
            <Button type="outline" title="Outline Button" onClick={() => alert('Clicked!')} />
            <Button type="success" title="Success Button" onClick={() => alert('Clicked!')} />
            <Button type="outline-destructive" title="Destructive" onClick={() => alert('Clicked!')} />
            <Button type="brand" title="Disabled" onClick={() => {}} disabled />
          </Box>
        );

      case 'Back':
        return (
          <Box>
            <Text fontSize="sm" mb={2} color="gray.600">
              Default (navigates back in history):
            </Text>
            <Back />
            <Text fontSize="sm" mt={4} mb={2} color="gray.600">
              With custom onClick:
            </Text>
            <Back onClick={() => alert('Custom back action!')} />
          </Box>
        );

      case 'ConsultantImage':
        return (
          <Box display="flex" gap={4} flexWrap="wrap">
            <Box>
              <Text fontSize="sm" mb={2} color="gray.600">With image URL:</Text>
              <ConsultantImage
                name="Anna Andersson"
                url="https://i.pravatar.cc/150?img=5"
              />
            </Box>
            <Box>
              <Text fontSize="sm" mb={2} color="gray.600">Fallback (initials):</Text>
              <ConsultantImage
                name="Anna Andersson"
              />
            </Box>
            <Box>
              <Text fontSize="sm" mb={2} color="gray.600">Small size:</Text>
              <ConsultantImage
                name="Anna Andersson"
                size="70px"
                placeholderFontSize="sm"
              />
            </Box>
          </Box>
        );

      case 'ConsultantProfile':
        return (
          <Box>
            <Text fontSize="sm" mb={2} color="gray.600">Full profile card:</Text>
            <ConsultantProfile
              consultant={mockConsultant}
              hasCustomerExperience={true}
            />
            <Text fontSize="sm" mt={4} mb={2} color="gray.600">Small variant:</Text>
            <ConsultantProfile
              consultant={mockConsultant}
              size="s"
            />
          </Box>
        );

      case 'CompetencePills':
        return (
          <Box>
            <Text fontSize="sm" mb={2} color="gray.600">Job competence badges:</Text>
            <CompetencePills competences={mockJobCompetences} />
          </Box>
        );

      case 'ConsultantCompetencePills':
        return (
          <Box>
            <Text fontSize="sm" mb={2} color="gray.600">Consultant competence badges:</Text>
            <ConsultantCompetencePills competences={mockConsultant.profile.competences} />
          </Box>
        );

      case 'ConsultantAvatar':
        return (
          <Box display="flex" gap={4} flexWrap="wrap">
            <Box>
              <Text fontSize="sm" mb={2} color="gray.600">Accepted:</Text>
              <ConsultantAvatar
                imageUrl="https://i.pravatar.cc/75?img=5"
                name="Anna Andersson"
                status="ACCEPTED"
                consultantId="123"
                jobId="456"
                customerId="789"
                organizationId="org1"
              />
            </Box>
            <Box>
              <Text fontSize="sm" mb={2} color="gray.600">Pending:</Text>
              <ConsultantAvatar
                imageUrl="https://i.pravatar.cc/75?img=8"
                name="Erik Eriksson"
                status="PENDING_ANSWER"
              />
            </Box>
            <Box>
              <Text fontSize="sm" mb={2} color="gray.600">Declined:</Text>
              <ConsultantAvatar
                imageUrl="https://i.pravatar.cc/75?img=12"
                name="Maria Nilsson"
                status="DECLINED"
              />
            </Box>
          </Box>
        );

      default:
        return (
          <Text fontSize="sm" color="gray.600">
            Component preview not yet implemented
          </Text>
        );
    }
  };

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
      <Heading size="md" mb={2}>
        {name}
        <Badge ml={2} colorScheme="green">Migrated ✓</Badge>
      </Heading>

      <Code fontSize="sm" mb={2}>
        src/components/{category.toLowerCase()}/{name}.tsx
      </Code>

      <Box mt={4} p={4} bg="gray.50" borderRadius="md">
        <Heading size="sm" mb={2}>Preview:</Heading>
        {renderPreview()}
      </Box>
    </Box>
  );
}

export default Showcase;
